import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import { RCS_BUTTON_TYPES, RcsButtonType, RcsPreviewCard, RcsMessageType } from 'src/app/models/Rcs';
import { brandInitial } from 'src/app/shared/utils/rcs-rules';

/** Botão do card na prévia. `target` é o destino de verdade (link, telefone, código Pix). */
export interface RcsPhoneButton {
  type: RcsButtonType;
  label: string;
  target?: string | null;
}

/** Marca no topo da conversa (o agente). */
export interface RcsPhoneBrand {
  name?: string | null;
  color?: string | null;
  logo_url?: string | null;
}

/**
 * O celular da prévia do RCS, no visual do Google Messages: a conversa com a marca (Basic
 * ou o card do Single) ou o SMS de quem não tem RCS. Saiu de dentro do editor (02/11/2026)
 * para o relatório do envio mostrar o mesmo celular com o que o cliente mandou.
 *
 * `interactive` liga os botões do card: "Abrir link" abre o destino numa aba nova, "Ligar"
 * mostra o telefone (e liga, no celular) e "Copiar código Pix" copia e mostra o código.
 * No editor fica desligado: lá o botão é só desenho.
 */
@Component({
  selector: 'app-rcs-phone',
  templateUrl: './rcs-phone.component.html',
})
export class RcsPhoneComponent implements OnChanges {
  @Input() view: 'rcs' | 'sms' = 'rcs';
  @Input() type: RcsMessageType = 'basic';
  @Input() brand: RcsPhoneBrand | null = null;
  @Input() title: string | null = null;
  @Input() mediaUrl: string | null = null;
  @Input() text = '';
  @Input() smsText = '';
  @Input() smsLength = 0;
  @Input() buttons: RcsPhoneButton[] = [];
  @Input() cards: RcsPreviewCard[] = [];
  @Input() interactive = false;
  /** Rodapé das bolhas ("agora", no editor; a data, no relatório). */
  @Input() when = 'agora';

  /** Botão de "Ligar"/"Pix" aberto: mostra o destino embaixo do card. */
  revealed: string | number | null = null;
  copied: string | number | null = null;
  private actionsSignature = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.buttons || changes.cards) {
      const signature = JSON.stringify([this.buttons, this.cards]);
      if (signature !== this.actionsSignature) {
        this.revealed = null;
        this.copied = null;
        this.actionsSignature = signature;
      }
    }
  }

  initial(name: string | null | undefined): string {
    return brandInitial(name || '');
  }

  icon(type: RcsButtonType): string {
    return RCS_BUTTON_TYPES.find(item => item.value === type)?.icon || 'fa-hand-pointer-o';
  }

  get hasTitle(): boolean {
    return !!(this.title || '').trim();
  }

  /** Só link http(s) vira `<a>`: o destino é dado do cliente. */
  linkOf(button: RcsPhoneButton): string | null {
    const target = (button.target || '').trim();
    return button.type === 'url' && /^https?:\/\//i.test(target) ? target : null;
  }

  cardButton(button: RcsPreviewCard['buttons'][number]): RcsPhoneButton {
    return { type: button.type, label: button.label, target: button.target ?? button.resolved ?? button.value };
  }

  telOf(button: RcsPhoneButton): string | null {
    const digits = (button.target || '').replace(/[^\d+]/g, '');
    return digits ? 'tel:' + digits : null;
  }

  press(button: RcsPhoneButton, index: string | number): void {
    if (!this.interactive || button.type === 'url') {
      return;
    }

    this.revealed = this.revealed === index ? null : index;
    this.copied = null;

    if (button.type === 'pix' && this.revealed === index && button.target) {
      const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
      clipboard?.writeText(button.target).then(() => this.copied = index, () => {});
    }
  }
}
